import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PreviewLanguageTranslationData, Translated } from '@app/models';
import { ISegmentAnalyzerData, ISegmentAnalyzerLanguageData, TRANSLATION_SEGMENT_THRESHOLD } from '@app/models/segment-analyzer.model';
import { AnalyticService } from '@app/core';
import { AnalyticLogLabels } from '@app/shared';

@Component({
    selector: 'bh-language-translation',
    templateUrl: './language-translation.component.html',
    styleUrls: ['./language-translation.component.scss'],
    standalone: false
})
export class LanguageTranslationComponent implements OnInit, OnDestroy {
    readonly TRANSLATION_SEGMENT_THRESHOLD = TRANSLATION_SEGMENT_THRESHOLD;
    private readonly unsubscribeAll$ = new Subject<void>();
    @Input('isChild') isChild: boolean = false;
    @Input('orgLanguages') orgLanguages!: string[];
    @Input('translatedData') translatedData!: Translated[];
    @Input('disabled') disabled: boolean = false;
    @Input('previewTranslationEnabled') previewTranslationEnabled: boolean = false;
    @Input() segmentAnalyzerData: ISegmentAnalyzerData | null = null;
    @Input() isAnalyzerLoading: boolean = false;
    previewTranslationData: PreviewLanguageTranslationData | null = null;
    @Output('fetchTranslationByLang') fetchTranslationByLangEmitter = new EventEmitter<any>();
    @Output('translationByLangDisable') translationByLangDisableEmitter = new EventEmitter<any>();
    @Output('simplifyForDelivery') simplifyForDeliveryEmitter = new EventEmitter<string>();
    translationForm!: UntypedFormGroup;
    defaultFormValues!: string;
    activeSimplifyLanguage: string | null = null;

    constructor(
        private readonly _formBuilder: UntypedFormBuilder, 
        private readonly _elementRef: ElementRef,
        private readonly _analyticService: AnalyticService
    ) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (this.activeSimplifyLanguage) {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            const clickedInside = this._elementRef.nativeElement.contains(target);
            if (!clickedInside) {
                this.activeSimplifyLanguage = null;
            }
        }
    }

    ngOnInit(): void {
        this.createForm();
    }

    ngOnDestroy(): void {
        this.unsubscribeAll$.next();
        this.unsubscribeAll$.complete();
    }

    /**
     * To create a dynamic formgroup from array of languages
     * @returns Formgroup with dynamic language groups
     */
    private getTranslationFormGroup(): UntypedFormGroup {
        let formObj: { [k: string]: any } = {};

        this.orgLanguages.forEach(langItem => {
            formObj[langItem] = this._formBuilder.group(
                {
                    autoTranslate: [true],
                    message: ['', [Validators.maxLength(750)]]
                },
                { validator: this.checkTranslate() }
            );
            (formObj[langItem] as UntypedFormGroup).controls['autoTranslate'].valueChanges
                .pipe(takeUntil(this.unsubscribeAll$))
                .subscribe(langItemValue => {
                    this.translationByLangDisableEmitter.emit({language: langItem, value: langItemValue});
                });
        });

        return formObj as UntypedFormGroup;
    }

    /**
     * Create translation form with array of languages
     * Store default intiated value in string
     */
    createForm(data?: Translated[]) {
        if (data?.length) this.translatedData = data;
        this.translationForm = this._formBuilder.group(this.getTranslationFormGroup());
        if (this.translatedData?.length) {
            this.translatedData.forEach(data => {
                this.translationForm.patchValue({ 
                    [data.language]: { autoTranslate: false, message: data.message} });
            });
        }
        this.defaultFormValues = JSON.stringify(this.translationForm.value);
        this.previewTranslationData = null;
    }

    /** to validate the translation form */
    checkTranslate() {
        return (translation: UntypedFormGroup) => {
            const ctrl1 = translation.get('autoTranslate')?.value;
            const ctrl2 = translation.get('message')?.value;
            if (!ctrl1 && ctrl2 === '') {
                return { emptyMessage: true };
            }
            return;
        };
    }

    /**
     * If user is opening/closing expansion panel in "Other Languages", this changes the toggle button state
     * @param language
     * @param value
     */
    changeToggleFor(language: string, value: boolean) {
        if (this.disabled) return;
        const control = this.translationFormControl[`${language}`].get('autoTranslate');
        control?.setValue(value);
    }

    handleToggleChange(language: string, value: boolean) {
        if ((language === this.previewTranslationData?.previewLanguage) && !value) {
            this.setPreviewTranslationData();
        }
    }

    setPreviewTranslationData(previewTranslationData: PreviewLanguageTranslationData | null = null) {
        this.previewTranslationData = previewTranslationData;
    }

    resetTranslationPreview() {
        this.previewTranslationData = null;
    }

    simplifyForDelivery(lang: string) {
        this.activeSimplifyLanguage = null;
        this.simplifyForDeliveryEmitter.emit(lang);
    }

    toggleSimplifyCard(lang: string) {
        if (this.activeSimplifyLanguage === lang) {
            this.activeSimplifyLanguage = null;
            return;
        }
        this.activeSimplifyLanguage = lang;
        this._analyticService.logAnalyticEvent(AnalyticLogLabels.InfoIconClicked, {
            event_category: 'Engagement',
            action: 'To view message',
            lang: lang
        });
    }

    fetchTranslationbyLanguage(lang: string) {
        const analyzerLang = this.getLanguageAnalyzerData(lang);
        if (analyzerLang?.translatedMessage) {
            this.setPreviewTranslationData({
                previewLanguage: lang,
                translatedMessage: analyzerLang.translatedMessage,
                errorData: null
            });
            return;
        }
        this.fetchTranslationByLangEmitter.emit(lang);
    }

    getLanguageAnalyzerData(lang: string) {
        return this.segmentAnalyzerData?.languages?.find((l: ISegmentAnalyzerLanguageData) => l.language.toLowerCase() === lang.toLowerCase());
    }

    get translationFormControl() {
        return this.translationForm.controls;
    }

    get hasChanges() {
        return this.defaultFormValues != JSON.stringify(this.translationForm.value);
    }

    trackByFn(index: number, item: any): number { 
      return index; 
    }
  
}
